import { supabase } from "@/lib/supabase/client";
import type { ProvisionCategory, StructuredValue } from "@argus/engine";

export type ReviewStatus = "pending" | "confirmed" | "edited" | "rejected";

export interface ProvisionRow {
  id: string;
  tenant_id: string;
  document_id: string;
  fund_id: string;
  lp_id: string | null;
  category: ProvisionCategory;
  subcategory: string | null;
  raw_text: string;
  summary: string | null;
  structured_value: StructuredValue | null;
  page_number: number | null;
  confidence: number | null;
  review_status: ReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  is_active: boolean;
  created_at: string;
}

const COLS =
  "id, tenant_id, document_id, fund_id, lp_id, category, subcategory, raw_text, summary, structured_value, page_number, confidence, review_status, reviewed_by, reviewed_at, is_active, created_at";

export async function listProvisionsByDocument(documentId: string): Promise<ProvisionRow[]> {
  const { data, error } = await supabase
    .from("provisions")
    .select(COLS)
    .eq("document_id", documentId)
    .order("page_number", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true });
  if (error) throw new Error(`listProvisionsByDocument: ${error.message}`);
  return data ?? [];
}

export async function listProvisionsByFund(
  tenantId: string,
  fundId: string,
  opts: { activeOnly?: boolean } = {},
): Promise<ProvisionRow[]> {
  let q = supabase.from("provisions").select(COLS).eq("tenant_id", tenantId).eq("fund_id", fundId);
  // HITL gate (build doc §1.4 / migration 0010): "active" means human-reviewed,
  // not merely "not rejected". A freshly-extracted 'pending' provision must not
  // reach the MFN engine, the conflict radar, the simulator, or the LP matrix
  // until a human confirms or edits it.
  if (opts.activeOnly) q = q.eq("is_active", true).in("review_status", ["confirmed", "edited"]);
  const { data, error } = await q;
  if (error) throw new Error(`listProvisionsByFund: ${error.message}`);
  return data ?? [];
}

export interface PendingProvisionRow extends ProvisionRow {
  document_title: string;
}

/**
 * All pending-review provisions across the tenant, with their source
 * document's title, for the review-queue page. Not fund-scoped — the queue is
 * a single cross-fund worklist.
 */
export async function listPendingProvisionsByTenant(
  tenantId: string,
): Promise<PendingProvisionRow[]> {
  const { data, error } = await supabase
    .from("provisions")
    .select(`${COLS}, document:documents(title)`)
    .eq("tenant_id", tenantId)
    .eq("review_status", "pending")
    .order("created_at", { ascending: true });
  if (error) throw new Error(`listPendingProvisionsByTenant: ${error.message}`);
  return (data ?? []).map((row) => {
    const { document, ...rest } = row as unknown as ProvisionRow & {
      document: { title: string } | null;
    };
    return { ...rest, document_title: document?.title ?? "Untitled document" };
  });
}

export interface InsertProvisionInput {
  tenant_id: string;
  document_id: string;
  fund_id: string;
  lp_id: string | null;
  category: ProvisionCategory;
  raw_text: string;
  summary: string;
  structured_value: StructuredValue | null;
  page_number: number;
  confidence: number;
  metadata?: Record<string, unknown>;
}

export async function insertProvisions(rows: InsertProvisionInput[]): Promise<ProvisionRow[]> {
  const { data, error } = await supabase.from("provisions").insert(rows).select(COLS);
  if (error) throw new Error(`insertProvisions: ${error.message}`);
  return data ?? [];
}

export async function deleteProvisionsForDocument(documentId: string): Promise<void> {
  const { error } = await supabase.from("provisions").delete().eq("document_id", documentId);
  if (error) throw new Error(`deleteProvisionsForDocument: ${error.message}`);
}

export interface ReviewPatch {
  review_status: ReviewStatus;
  category?: ProvisionCategory;
  summary?: string;
  structured_value?: StructuredValue | null;
}

export async function reviewProvision(
  id: string,
  userId: string,
  patch: ReviewPatch,
): Promise<ProvisionRow> {
  const { data, error } = await supabase
    .from("provisions")
    .update({ ...patch, reviewed_by: userId, reviewed_at: new Date().toISOString() })
    .eq("id", id)
    .select(COLS)
    .single();
  if (error) throw new Error(`reviewProvision: ${error.message}`);

  // HITL cascade (build doc §1.4 / migration 0010): a rejected provision can no
  // longer support any MFN trigger it fired — dismiss the open ones so the MFN
  // board never points a reviewer back at rejected source text. Obligations
  // need no cascade: they are join-gated by the source provision's
  // review_status (v_compliance_calendar and every obligation-reading query).
  if (patch.review_status === "rejected") {
    const { error: cascadeError } = await supabase
      .from("mfn_triggers")
      .update({
        status: "dismissed",
        resolution_notes: "Source provision rejected in human review",
        resolved_at: new Date().toISOString(),
        resolved_by: userId,
      })
      .eq("triggering_provision_id", id)
      .eq("status", "open");
    if (cascadeError) throw new Error(`reviewProvision/cascade: ${cascadeError.message}`);

    // The rejected provision may itself BE an MFN clause (the holder's right,
    // not the triggering term). Triggers derived from that clause are equally
    // unsupported — dismiss those too.
    const { data: clauseRows, error: clauseError } = await supabase
      .from("mfn_clauses")
      .select("id")
      .eq("provision_id", id);
    if (clauseError) throw new Error(`reviewProvision/cascade-clauses: ${clauseError.message}`);
    const clauses = (clauseRows ?? []) as { id: string }[];
    if (clauses.length > 0) {
      const { error: clauseCascadeError } = await supabase
        .from("mfn_triggers")
        .update({
          status: "dismissed",
          resolution_notes: "MFN clause's source provision rejected in human review",
          resolved_at: new Date().toISOString(),
          resolved_by: userId,
        })
        .in(
          "mfn_clause_id",
          clauses.map((c) => c.id),
        )
        .eq("status", "open");
      if (clauseCascadeError)
        throw new Error(`reviewProvision/cascade-clause-triggers: ${clauseCascadeError.message}`);
    }
  }

  return data;
}

export interface ProvisionCounts {
  pending: number;
  confirmed: number;
  edited: number;
  rejected: number;
}

export async function countProvisionsByReview(tenantId: string): Promise<ProvisionCounts> {
  const { data, error } = await supabase
    .from("provisions")
    .select("review_status")
    .eq("tenant_id", tenantId)
    .eq("is_active", true);
  if (error) throw new Error(`countProvisionsByReview: ${error.message}`);
  const counts: ProvisionCounts = { pending: 0, confirmed: 0, edited: 0, rejected: 0 };
  for (const row of (data ?? []) as { review_status: ReviewStatus }[]) {
    counts[row.review_status] += 1;
  }
  return counts;
}
